<template>
    <common-content-spacer height="medium">
        <div v-for="(m, i) in momentsArray" :key="i" class="schedule-timeline">
            <button v-for="(moment, index) in m" :key="index" v-bind="momentProps(moment)" @click="handleClick(moment)">
                <span class="schedule-timeline__tooltip" :data-tooltip="momentDate(moment)"></span>

                <span class="schedule-timeline__moment-label">
                    {{ momentDate(moment, true) }}
                </span>

                <svg v-if="momentIcon(moment)">
                    <use :xlink:href="momentIcon(moment)" />
                </svg>

                <div v-if="isSlotChanged(moment)" class="schedule-timeline__warning-container">
                    <common-icon
                        name="white-warning"
                        size="large"
                        :hintHeader="
                            $t('elements.availability.schedule_timeline.previusly', { hours: calculatePreviusHours(moment) })
                        "
                        :hintText="$t('elements.availability.schedule_timeline.changed_to')"
                    ></common-icon>
                    <span class="schedule-timeline__slot-label">
                        {{ momentDate(moment) }}
                    </span>
                </div>
            </button>
        </div>
    </common-content-spacer>
</template>

<script>
    import FormChangeableMixin from 'mixins/FormChangeableMixin'
    import dayjs from 'dayjs'

    export default {
        mixins: [FormChangeableMixin],

        props: {
            dayFrom: { type: Number, required: true },
            dayTo: { type: Number, required: true },
            appointmentLength: { type: Number, required: true },
        },

        data() {
            return {
                draftSlot: { from: undefined, to: undefined },
                momentsArray: [],
            }
        },

        computed: {
            originalSlots() {
                return this.$get(this.formData, this.valuePath, [])
            },

            moments() {
                const start = dayjs().startOf('day')
                const from = start.add(this.dayFrom, 'hour')
                const to = start.add(this.dayTo, 'hour')
                const hours = to.diff(from, 'hour')
                const moments = (hours * 60) / this.appointmentLength + 1

                return Array(moments)
                    .fill({})
                    .map((option, index) =>
                        start
                            .add(this.dayFrom, 'hour')
                            .add(this.appointmentLength * index, 'minute')
                            .diff(start, 'minute'),
                    )
            },

            slots() {
                return this.originalSlots.concat([this.draftSlot])
            },

            isDraftMode() {
                return this.draftSlot.from !== undefined || this.draftSlot.to !== undefined
            },
        },

        methods: {
            momentDate(moment, isShort = false) {
                return dayjs()
                    .startOf('day')
                    .add(moment, 'minute')
                    .format(isShort ? 'hh a' : 'hh:mm a')
            },

            isFullHour(moment) {
                return moment % 60 === 0
            },

            isSlotStart(moment) {
                return this.slots.some((slot) => moment === slot.from)
            },

            isSlotWithin(moment) {
                return this.slots.some((slot) => moment > slot.from && moment < slot.to)
            },

            isSlotEnd(moment) {
                return this.slots.some((slot) => moment === slot.to)
            },

            isSlotChanged(moment) {
                const slot = this.$get(this.formData, this.valuePath, []).find((s) => s.from === moment || s.to === moment)

                if (slot) {
                    if (this.isSlotStart(moment)) {
                        return !!slot.isFromChanged
                    } else if (this.isSlotEnd(moment)) {
                        return !!slot.isToChanged
                    }
                }
                return false
            },

            isSlotDraft(moment) {
                return [this.draftSlot.from, this.draftSlot.to].includes(moment)
            },

            isOverlappingSlot(from, to) {
                const testedSlot = {
                    from: Math.min(from, to),
                    to: Math.max(from, to),
                }

                return this.originalSlots.some(
                    (slot) =>
                        (testedSlot.from < slot.from && slot.from < testedSlot.to) ||
                        (testedSlot.from >= slot.from && testedSlot.from <= slot.to) ||
                        (testedSlot.to >= slot.from && testedSlot.to <= slot.to),
                )
            },

            addSlot(from, to) {
                this.change(
                    this.originalSlots.concat([
                        {
                            from: Math.min(from, to),
                            to: Math.max(from, to),
                        },
                    ]),
                )
            },

            removeSlot(moment) {
                this.change(
                    this.originalSlots.filter((slot) => {
                        return ![slot.from, slot.to].includes(moment) && !(moment > slot.from && moment < slot.to)
                    }),
                )
            },

            clearDraftSlot() {
                this.draftSlot = { from: undefined, to: undefined }
            },

            momentProps(moment) {
                return {
                    type: 'button',
                    class: {
                        'schedule-timeline__moment': true,
                        'schedule-timeline__moment--hour-full': this.isFullHour(moment),
                        'schedule-timeline__moment--hour-within': !this.isFullHour(moment),
                        'schedule-timeline__moment--slot-start': this.isSlotStart(moment),
                        'schedule-timeline__moment--slot-within': this.isSlotWithin(moment),
                        'schedule-timeline__moment--slot-end': this.isSlotEnd(moment),
                        'schedule-timeline__moment--slot-draft': this.isSlotDraft(moment),
                        'schedule-timeline__moment--slot-changed': this.isSlotChanged(moment),
                    },
                }
            },

            momentIcon(moment) {
                if (this.isSlotStart(moment)) {
                    return '#picture-slider-left'
                }

                if (this.isSlotEnd(moment)) {
                    return '#picture-slider-right'
                }

                return false
            },

            handleClick(moment) {
                const isSlotStart = this.isSlotStart(moment)
                const isSlotWithin = this.isSlotWithin(moment)
                const isSlotEnd = this.isSlotEnd(moment)
                const isOverlappingSlot = this.isOverlappingSlot(this.draftSlot.from, moment)

                if (this.isDraftMode) {
                    if (isSlotStart || isSlotWithin || isSlotEnd || isOverlappingSlot) {
                        this.clearDraftSlot()
                        return // Prevent creating slot that is overlapping the existing one.
                    }

                    this.addSlot(this.draftSlot.from, moment)
                    this.clearDraftSlot()
                    return
                }
                if (isSlotStart || isSlotEnd || this.shouldRemoveSlot(moment)) {
                    this.removeSlot(moment)
                    return
                }

                if (!this.isOverlappingSlot(moment, moment)) {
                    this.draftSlot.from = moment
                }
            },

            shouldRemoveSlot(moment) {
                return this.originalSlots.some((slot) => moment > slot.from && moment < slot.to)
            },

            calculatePreviusHours(moment) {
                const slot = this.$get(this.formData, this.valuePath, []).find((s) => s.from === moment || s.to === moment)
                if (slot.from === moment) {
                    return dayjs().startOf('day').add(slot.fromOldValue, 'minutes').format('hh:mm a')
                }
                return dayjs().startOf('day').add(slot.toOldValue, 'minutes').format('hh:mm a')
            },

            recalculateItemsWidth() {
                const elWrapper = document.getElementsByClassName('schedule__timeline')
                let minElWidth
                if (this.appointmentLength === 15) {
                    minElWidth = 15
                } else if (this.appointmentLength === 30) {
                    minElWidth = 23
                } else if (this.appointmentLength === 60) {
                    minElWidth = 35
                }

                if (elWrapper[0].offsetWidth > minElWidth * this.moments.length) {
                    this.reMapMomentsArray(1)
                } else {
                    this.reMapMomentsArray(2)
                }
            },

            reMapMomentsArray(numberOfArrays) {
                this.momentsArray = []
                const chunkSize = this.moments.length / numberOfArrays
                for (let i = 0; i < this.moments.length; i += chunkSize) {
                    this.momentsArray.push(this.moments.slice(i, i + chunkSize))
                }

                this.momentsArray.forEach((arr, index) => {
                    if (index < this.momentsArray.length && this.momentsArray[index + 1]?.length) {
                        this.momentsArray[index].push(this.momentsArray[index + 1][0])
                    }
                })

                this.setElementsWidth(numberOfArrays)
            },

            setElementsWidth(numberOfArrays) {
                setTimeout(() => {
                    const elWrapper = document.getElementsByClassName('schedule__timeline')
                    const elements = document.querySelectorAll('.schedule-timeline__moment')
                    let elWidth
                    if (numberOfArrays > 1) {
                        elWidth = elWrapper[0].offsetWidth / ((this.moments.length + numberOfArrays - 1) / numberOfArrays)
                    } else {
                        elWidth = elWrapper[0].offsetWidth / (this.moments.length / numberOfArrays)
                    }

                    const elTimeline = document.getElementsByClassName('schedule-timeline')
                    elTimeline.forEach((el) => {
                        el.style.setProperty('--width', `calc(100% - ${elWidth}px)`)
                        el.style.setProperty('--left', `${elWidth / 2}px`)
                    })

                    elements.forEach((item) => {
                        item.style.minWidth = `${elWidth}px`
                        item.style.maxWidth = `${elWidth}px`
                    })
                }, 0)
            },
        },

        watch: {
            originalSlots() {
                this.clearDraftSlot()
            },

            appointmentLength() {
                this.recalculateItemsWidth()
            },
        },

        mounted() {
            this.recalculateItemsWidth()
            window.addEventListener('resize', this.recalculateItemsWidth)
        },

        beforeDestroy() {
            window.removeEventListener('resize', this.recalculateItemsWidth)
        },
    }
</script>
